import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { cloneDeep } from 'lodash-es';
import {
  Actions,
  ofActionDispatched,
  ofActionErrored,
  ofActionSuccessful,
  Store,
} from '@ngxs/store';
import { OptionsSelector } from '@nibtravel/shared/data-access/options';
import { PlansAction } from '@nibtravel/shared/data-access/plans';
import {
  IQuoteInput,
  QuoteActions,
  QuoteState,
} from '@nibtravel/shared/data-access/quote';
import { QuoteConfigState } from '@nibtravel/shared/data-access/quote-config';
import { UiContextActions } from '@nibtravel/shared/data-access/ui-context';
import { IconSizes, IconType } from '@nibtravel/shared/ui/components/icon';
import { IconPosition } from '@nibtravel/shared/ui/components/link-icon';
import { ModalService } from '@nibtravel/shared/ui/components/modal';
import { SidebarService } from '@nibtravel/shared/ui/components/sidebar';
import { Qqc } from '@nibtravel/shared/util/e2e';
import { Features } from '@nibtravel/shared/util/feature-toggle';
import { FormHelper } from '@nibtravel/shared/util/form';
import {
  CancellationType,
  IPolicyHoldersInput,
  OptionType,
  QuoteStage,
} from '@nibtravel/shared/util/models';
import {
  IPolicyHolderStatus,
  PolicyHolderService,
} from '@nibtravel/shared/util/service';
import { BehaviorSubject, merge, of, Subject, timer, zip } from 'rxjs';
import {
  filter,
  map,
  mapTo,
  skipUntil,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import { QqcSelector } from './qqc.selector';
import { isAustralia } from '../../../../../shared/util/common/isAustralia';
import { Router } from '@angular/router';
import { InternationalDomesticService } from './qqc.international-domestic.service';
import { QqcRedirectToHorizontalService } from './qqc.redirect-to-horizontal.service';
import { NewRelicAnalytics } from '../../../../../shared/data-access/newrelic-analytics/src/lib/newrelic.action';

@Component({
  selector: 'nibtravel-qqc',
  templateUrl: './qqc.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QqcComponent implements OnInit, OnDestroy {
  @HostBinding('class')
  get hostClass() {
    const qqcDisplay = this.isStandalone ? 'qqc--horizontal' : 'qqc--vertical';
    return `qqc ${qqcDisplay}`;
  }

  @Input() isStandalone = false;

  e2eId = Qqc;

  icon = IconType;
  iconSize = IconSizes;
  position = IconPosition;
  modalId = 'ambiguousModal';
  QqcDestinationId = Features.QqcDestinationId;
  QqcTripTypeId = Features.QqcTripTypeId;
  QqcTravelDatesId = Features.QqcTravelDatesId;
  QqcTravellersAgeId = Features.QqcTravellersAgeId;
  QqcPrivateInsuranceId = Features.QqcPrivateInsuranceId;
  showDomesticInternationalAlert$ = new BehaviorSubject(false);
  domesticInternationalAlertModalId = 'domesticInternationalAlertModal';
  qqcForm = new FormGroup({});
  isEditMode!: boolean;

  destroy$ = new Subject<void>();
  startGetQuote$ = new Subject<void>();
  requesting$ = new BehaviorSubject<boolean>(false);
  showAmbiguity$ = new BehaviorSubject<boolean>(false);
  previousQuoteDestinations: string[] = [''];
  currentRoute = '';
  fromDomesticToInternational = true;
  get titleTranslation() {
    return this.isEditMode ? 'title-edit' : 'title';
  }

  get btnQuoteTranslation() {
    return this.isEditMode ? 'btn-quote-edit' : 'btn-quote';
  }

  constructor(
    private store: Store,
    private actions: Actions,
    private modalService: ModalService,
    private cd: ChangeDetectorRef,
    private el: ElementRef,
    private policyHolderHelper: PolicyHolderService,
    private sidebarService: SidebarService,
    private router: Router,
    private internationalDomesticService: InternationalDomesticService,
    private qqcRedirectToHorizontal: QqcRedirectToHorizontalService
  ) {}

  ngOnInit(): void {
    this.sidebarService
      .showSidebarHost()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => (this.isEditMode = isOpen));
    this.setupSubscriptions();

    if (this.isStandalone) {
      this.qqcRedirectToHorizontal.populateFormStateHandler();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupSubscriptions(): void {
    this.actions
      .pipe(
        ofActionDispatched(QuoteActions.UpdateFormStatus),
        skipUntil(timer(1000)),
        filter((value) => value.payload?.status === 'VALID'),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.store.dispatch(new UiContextActions.ResetQqcAlertMessage());
      });

    merge(
      this.actions.pipe(ofActionDispatched(QuoteActions.GetQuote), mapTo(true)),
      this.actions.pipe(ofActionErrored(QuoteActions.GetQuote), mapTo(false))
    )
      .pipe(skipUntil(timer(500)), takeUntil(this.destroy$))
      .subscribe(this.requesting$);

    const quoteSuccess$ = this.actions.pipe(
      ofActionSuccessful(QuoteActions.GetQuote)
    );
    const loadPlansSuccess$ = this.actions.pipe(
      ofActionSuccessful(PlansAction.LoadPlansData)
    );

    zip(quoteSuccess$, loadPlansSuccess$)
      .pipe(skipUntil(this.startGetQuote$), takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.isEditMode) {
          this.store.dispatch(new QuoteActions.NavigateQuoteStart());
        }
        this.requesting$.next(false);
        // when users clicked 'update details' button in the sidebar
        this.closeSidebar(undefined, 'update');
        const newQuoteInput: IQuoteInput = this.store.selectSnapshot((v) => v)
          .quote.input;
        this.sidebarService.updateLastInput(newQuoteInput);
      });

    this.sidebarService
      .getLastInput()
      .pipe(
        takeUntil(this.destroy$),
        map((v) => {
          return v.destinationCountryCodes;
        })
      )
      .subscribe((destinationCodes) => {
        this.previousQuoteDestinations = destinationCodes;
      });

    this.store
      .select(QuoteState.currentRoute)
      .pipe(takeUntil(this.destroy$))
      .subscribe((route) => {
        this.currentRoute = route;
      });
  }

  get showAustraliaAlert(): boolean {
    const homeCountry = this.store.selectSnapshot(
      QuoteState.destinationCountryCodes
    );

    const _isAustralia = isAustralia(homeCountry);
    return _isAustralia;
  }

  onSubmit(): void {
    try {
      this.startGetQuote$.next();

      this.store
        .dispatch([
          new UiContextActions.ResetQqcAlertMessage(),
          new QuoteActions.UpdatePolicyholder(),
        ])
        .subscribe(() => {
          FormHelper.validateAllFormFields(this.qqcForm);
          if (this.qqcForm.valid && !this.qqcForm.pristine) {
            const data = [
              { departureDate: this.qqcForm.get('departureDate')?.value },
              { coverEndDate: this.qqcForm.get('coverEndDate')?.value },
              {
                destinationCountryCodes: this.qqcForm.get(
                  'destinationCountryCodes'
                )?.value,
              },
              {
                travellerAges: this.qqcForm
                  .get('policyHolders')
                  ?.value.map((policyHolder: any) => policyHolder.age)
                  .join(', '),
              },
            ];
            this.store.dispatch(
              new NewRelicAnalytics.SetNewRelicActionAttributes(data)
            );
            this.canGetQuote();
          } else {
            FormHelper.scrollToFirstInvalidControl(this.el.nativeElement);
          }
        });
    } catch (e) {
      // console.log('submit error',e);
    } finally {
      const getErrorMessages = (domElements: any) =>
        Array.from(domElements).map((el: any) => el.innerText);
      timer(1000).subscribe(() => {
        const errors = getErrorMessages(
          document.querySelectorAll('.input-error-message')
        );
        this.store.dispatch([
          new QuoteActions.SubmitQuote({ errors }),
          new QuoteActions.UpdateQuote({ errors }),
        ]);
      });
    }
  }

  /**
   * On confirming ambiguity close modal and check if can get quote
   */
  onAmbiguityConfirm(): void {
    this.closeModal();
    this.canGetQuote();
  }

  onChangeDestinationConfirm(): void {
    this.onCloseChangeDestinationAlertModal(false);
    this.sidebarService.close();
    // navigate to kosmos (the horizontal qqc)
    this.store.dispatch(new QuoteActions.NavigateToQuote(true));
  }

  /**
   * This logic needs to be moved away from here.
   * All this component should be doing is simply requesting whether it needs to display
   * the age ambiguity modal. Everything else its not responsibility of the component.
   */
  canGetQuote(): void {
    // eslint-disable-next-line no-constant-condition
    /**
     * check if destination changed from international to domestic or from domestic to international
     * */
    if (!['', '/', 'plans'].includes(this.currentRoute)) {
      const {
        changeFromInternationToDomestic,
        changeFromDomesticToInternational,
      } = this.internationalDomesticService.getInternationalDomesticStatus(
        this.previousQuoteDestinations
      );
      switch (true) {
        case changeFromInternationToDomestic:
          // change from international to domestic
          this.fromDomesticToInternational = false;
          this.openDomesticInternationalAlertModal();
          return;
        case changeFromDomesticToInternational:
          // change from domestic to international
          this.fromDomesticToInternational = true;
          this.openDomesticInternationalAlertModal();
          return;
      }
    }

    const policyHolders = this.store.selectSnapshot(
      QuoteState.getPolicyHolders
    );
    const country = this.store.selectSnapshot(QuoteState.homeCountry);
    if (country) {
      const quoteType = this.store.selectSnapshot(QuoteState.quoteType);
      const policyholderConfig = this.store.selectSnapshot((state) =>
        QuoteConfigState.policyHolderConfig(
          state.quoteConfig,
          country,
          quoteType
        )
      );
      const phStatus: IPolicyHolderStatus = this.policyHolderHelper.canGetQuote(
        policyHolders,
        policyholderConfig
      );
      if (phStatus.isAmbiguous) {
        this.showAmbiguity$.next(true);
        this.cd.detectChanges();
        this.modalService.open(this.modalId);
        return;
      }
      if (phStatus.countError) {
        return;
      }
      this.getQuote(policyHolders);
    }
  }

  closeModal(): void {
    this.modalService.close(this.modalId);
    this.showAmbiguity$.next(false);
  }

  onCloseChangeDestinationAlertModal(restoreInput?: boolean): void {
    this.modalService.close(this.domesticInternationalAlertModalId);
    this.showDomesticInternationalAlert$.next(false);
    if (restoreInput) {
      const newInput = this.store.selectSnapshot((v) => v).quote.input;
      const result = cloneDeep(newInput);
      result.destinationCountryCodes = this.previousQuoteDestinations;
      this.store.dispatch(new QuoteActions.ResetToOriginalQuoteInput(result));
    }
  }

  openDomesticInternationalAlertModal(): void {
    this.showDomesticInternationalAlert$.next(true);
    this.cd.detectChanges();
    this.modalService.open(this.domesticInternationalAlertModalId);
  }

  /**
   * On Edit mode, the qqc is presented inside the sidebar.
   * This function handles the close when the cancel button is clicked.
   */
  closeSidebar(event?: Event, text?: string): void {
    if (event) {
      event.preventDefault();
    }
    this.sidebarService.close();
    if (text) {
      this.sidebarService.currentClickedButton$.next(text);
    }
  }

  private getQuote(policyHolders: IPolicyHoldersInput[]): void {
    if (this.isStandalone) {
      this.store.dispatch(new QuoteActions.NavigateToQuote());
      return;
    }

    // Pre Quote Actions
    const actions = [];
    const currentStage = this.store.selectSnapshot(QqcSelector.quoteStage);

    if (!this.isEditMode) {
      actions.push(new QuoteActions.QqcReset());
    } else {
      const planId = this.store.selectSnapshot(QuoteState.planId);
      if (planId) {
        actions.push(
          new PlansAction.CalculateImplicitCancellationValue(
            planId,
            policyHolders
          ),
          new QuoteActions.SetQuoteStage(QuoteStage.Initial)
        );
      }

      // When the user goes to traveller details and populate the form, the values get assigned to home address
      // The Quote Api doesn't like values on home address when we change the stage to QuoteStage.Initial
      // This would happen on edit trip if the user goes to traveller details and come back to either plans or options page.
      const isHomeAddressDirty = this.store.selectSnapshot(
        QqcSelector.isHomeAddressDirty
      );
      if (isHomeAddressDirty) {
        actions.push(new QuoteActions.ResetHomeAddress());
      }
    }

    //reset cancellation option
    actions.push(
      new QuoteActions.SetTripCancellationOption(
        OptionType.TripCancellationFixed as CancellationType,
        1000
      )
    );

    // This will need to do for the time being...
    this.store
      .dispatch(actions)
      .pipe(
        switchMap(() => {
          const implicitCancellationValue = this.store.selectSnapshot(
            OptionsSelector.implicitCancellationValue
          );
          const config = this.store.selectSnapshot(
            OptionsSelector.cancellationOptionConfig
          );

          if (config && implicitCancellationValue) {
            return this.store.dispatch([
              new QuoteActions.SetTripCancellationOption(
                config.optionType,
                implicitCancellationValue
              ),
            ]);
          }
          return of(true);
        }),
        switchMap(() => {
          return this.store.dispatch(new QuoteActions.GetQuote());
        })
      )
      .subscribe(() => {
        if (this.isEditMode) {
          this.store.dispatch(new QuoteActions.SetQuoteStage(currentStage));
        }
      });
  }
}
